import React, { useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '@/theme/ThemeProvider';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';

interface KycRequiredProps {
  message?: string;
  isModal?: boolean;
  onClose?: () => void;
}

const KycRequired: React.FC<KycRequiredProps> = ({ message, isModal = false, onClose }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleKycVerification = () => {
    if (isModal && onClose) {
      onClose();
    }
    // KYC 인증 페이지로 이동
    navigate('/settings?tab=2');
  };

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const content = (
    <ContentWrapper>
      <IconContainer isDarkMode={isDarkMode}>
        <SecurityIcon>🔒</SecurityIcon>
      </IconContainer>
      
      <Title isDarkMode={isDarkMode}>
        {i18next.language === 'ko' ? 'KYC 인증이 필요합니다' : 'KYC Verification Required'}
      </Title>
      
      <Description isDarkMode={isDarkMode}>
        {message || (i18next.language === 'ko' 
          ? (
            <>
              고급 보안 기능을 사용하기 위해서는 KYC 인증을 완료해야 합니다.<br/>
              본인 확인을 통해 안전한 거래 환경을 제공합니다.<br/><br/>
              관리자 승인이 완료되면 2단계 인증 등 고급 보안 기능을<br/>
              이용하실 수 있습니다.
            </>
          )
          : (
            <>
              KYC verification is required to use advanced security features. Complete identity verification for a safe trading environment.<br/><br/>
              Once approved by the administrator, you will be able to use advanced security features such as two-factor authentication.
            </>
          )
        )}
      </Description>
      
      <ButtonGroup>
        <VerifyButton onClick={handleKycVerification} isDarkMode={isDarkMode}>
          {i18next.language === 'ko' ? 'KYC 인증하기' : 'Start KYC Verification'}
        </VerifyButton>
        
        <BackButton onClick={handleClose} isDarkMode={isDarkMode}>
          {i18next.language === 'ko' ? '닫기' : 'Close'}
        </BackButton>
      </ButtonGroup>
    </ContentWrapper>
  );

  if (isModal) {
    return content;
  }

  return (
    <Container isDarkMode={isDarkMode}>
      {content}
    </Container>
  );
};

const Container = styled.div<{ isDarkMode: boolean }>`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: ${({ isDarkMode }) => isDarkMode ? '#080D0C' : '#f9fafb'};
`;

const ContentWrapper = styled.div`
  max-width: 500px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const IconContainer = styled.div<{ isDarkMode: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ isDarkMode }) => isDarkMode ? '#1f2937' : '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const SecurityIcon = styled.div`
  font-size: 36px;
  opacity: 0.8;
`;

const Title = styled.h2<{ isDarkMode: boolean }>`
  font-size: 24px;
  font-weight: 600;
  color: ${({ isDarkMode }) => isDarkMode ? '#ffffff' : '#1f2937'};
  margin: 0;
  line-height: 1.3;
`;

const Description = styled.p<{ isDarkMode: boolean }>`
  font-size: 16px;
  color: ${({ isDarkMode }) => isDarkMode ? '#9ca3af' : '#6b7280'};
  line-height: 1.6;
  margin: 0;
  max-width: 400px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
  margin-top: 8px;
`;

const VerifyButton = styled.button<{ isDarkMode: boolean }>`
  width: 100%;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
`;

const BackButton = styled.button<{ isDarkMode: boolean }>`
  width: 100%;
  padding: 12px 24px;
  border: 1px solid ${({ isDarkMode }) => isDarkMode ? '#4b5563' : '#d1d5db'};
  border-radius: 8px;
  background: transparent;
  color: ${({ isDarkMode }) => isDarkMode ? '#e5e7eb' : '#374151'};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: ${({ isDarkMode }) => isDarkMode ? '#374151' : '#f9fafb'};
  }
`;

export default KycRequired; 